import { useEffect, useState } from 'react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { TenantSlugDiscoveryResponse } from 'api';
import {
  AUTH_LOADING_LABELS,
  IS_LOGGING_OUT_KEY,
  SESSION_VERIFICATION_RETRY_DELAY_MS,
  SIGN_IN_REASON,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { LoadingState } from 'components/ui';
import { useAuthSessionRestoreContext } from 'context';
import {
  useIsUserLoggedIn,
  useTenantDiscovery,
  useVerifyAccessToken,
} from 'pages/Auth/hooks';
import {
  buildSignInUrl,
  clearStoredAuthTokens,
  getStoredAccessToken,
  isAuthLogoutInProgress,
  TransientRefreshError,
} from 'pages/Auth/utils';

interface CustomRouteElementProps {
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
}

// Helper component that returns the element for use in Route components
export const CustomRouteElement = (props: CustomRouteElementProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const verifyAccessToken = useVerifyAccessToken();

  // Prevents the discovery effect below from re-firing once the SSO-vs-normal-login flow is resolved.
  const [isPendingFlowResolution, setIsPendingFlowResolution] = useState(true);

  const { discoverTenantBySlug, needsDiscovery } = useTenantDiscovery({ location: globalThis.location.href });

  const [hasExpiredSession, setHasExpiredSession] = useState(false);
  const [hasVerifiedToken, setHasVerifiedToken] = useState(false);
  const [hasRetriedSessionVerification, setHasRetriedSessionVerification] = useState(false);
  const { accessToken, isRestoringRememberedSession } = useAuthSessionRestoreContext();
  const location = useLocation();
  const isLoggingOut = isAuthLogoutInProgress();

  const { layout: Layout, component: Component, isPrivate = false } = props;

  const hasAccessToken = Boolean(accessToken);

  useEffect(() => {
    // Token storage can outlive in-memory user state after refreshes, so verify before redirecting.
    if (hasAccessToken && !isUserLoggedIn && !hasVerifiedToken) {
      let isMounted = true;
      let retryTimeout: ReturnType<typeof setTimeout> | undefined;

      const verifyStoredSession = async () => {
        let isTransient = false;
        try {
          await verifyAccessToken();
        } catch (error) {
          if (error instanceof TransientRefreshError) {
            isTransient = true;
          }
        }
        if (!isMounted) {
          return;
        }
        if (isTransient) {
          if (!hasRetriedSessionVerification) {
            // Retry without marking the route as verified
            retryTimeout = setTimeout(() => {
              setHasRetriedSessionVerification(true);
            }, SESSION_VERIFICATION_RETRY_DELAY_MS);
            return;
          }
          // Already retried once — clear the session so the expired path fires correctly.
          clearStoredAuthTokens();
        }
        // Only a session that existed and then died is an expiry. Arriving at a private route with no
        // token at all is an ordinary sign-in, and must not claim the user was logged out.
        if (!getStoredAccessToken() && !isLoggingOut) {
          setHasExpiredSession(true);
        }
        setHasVerifiedToken(true);
      };

      verifyStoredSession();

      return () => {
        isMounted = false;
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
      };
    }

    return undefined;
  }, [
    hasAccessToken,
    hasRetriedSessionVerification,
    hasVerifiedToken,
    isLoggingOut,
    isUserLoggedIn,
    verifyAccessToken,
  ]);

  useEffect(() => {
    if (!hasAccessToken) {
      setHasVerifiedToken(false);
    }
  }, [hasAccessToken]);

  useEffect(() => {
    const verifyFlowToFollow = async () => {
      const flow = await discoverTenantBySlug();
      const postDiscoveryFlow = flow?.post_discovery_flow as string | undefined;
      if (flow && postDiscoveryFlow === TenantSlugDiscoveryResponse.post_discovery_flow.SAML) {
        // browser redirect to SSO login URL
        globalThis.location.href = flow.login_url as string;
      } else {
        setIsPendingFlowResolution(false);
      }
    };
    if (
      !isUserLoggedIn
      && !hasAccessToken
      && !isRestoringRememberedSession
      && needsDiscovery
      && isPendingFlowResolution
      && !isLoggingOut
    ) {
      verifyFlowToFollow();
    }
  }, [
    discoverTenantBySlug,
    hasAccessToken,
    isLoggingOut,
    isPendingFlowResolution,
    isRestoringRememberedSession,
    isUserLoggedIn,
    needsDiscovery,
  ]);

  useEffect(() => {
    // This executes after we've been redirected to logIn,
    // so we can remove the helper flag
    if (isLoggingOut) {
      localStorage.removeItem(IS_LOGGING_OUT_KEY);
    }
  }, [isLoggingOut]);

  // Deliberately unwrapped: a private Layout must never mount without an access token. FirmBaseLayout
  // fetches the firm list from a mount effect keyed only on dispatch, and that request shares
  // singleFlightTokenRefresh through the retry wrapper. A transient refresh rethrows the 401, getFirmList
  // resolves with the error object, and the layout leaves firmListStatus at IDLE — which renders as the
  // primary-nav skeleton, since there is no error state. React keeps the same Layout instance when this
  // branch falls through, so that mount effect never re-runs and the nav never recovers.
  if (isRestoringRememberedSession && !accessToken && isPrivate) {
    return <LoadingState label={AUTH_LOADING_LABELS.session} />;
  }

  // Protect private page from unauthorized users
  if (!isUserLoggedIn && !accessToken && isPrivate) {
    return (
      <Navigate
        to={buildSignInUrl({
          reason: hasExpiredSession ? SIGN_IN_REASON.SESSION_EXPIRED : null,
          // Explicit logout should land on a fresh sign-in screen instead of restoring the protected page.
          returnTo: isLoggingOut ? null : `${location.pathname}${location.search}`,
          view: SIGN_IN_VIEW.IDENTIFIER,
        })}
        replace
      />
    );
  }

  if (Layout && Component) {
    return (
      <Layout>
        <Component />
      </Layout>
    );
  }

  if (Layout) {
    return (
      <Layout>
        <Outlet />
      </Layout>
    );
  }

  return Component ? <Component /> : <></>;
};

// Legacy interface for backward compatibility
interface CustomRouteProps {
  path: string;
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
  exact?: boolean;
}

// This is kept for compatibility but should not be used as direct child of Routes
const CustomRoute = (props: CustomRouteProps) => (
  // This component is deprecated in React Router v6 context
  // Use CustomRouteElement instead
  <CustomRouteElement {...props} />
);
export default CustomRoute;
